import React from "react";
import styles from "../../_css/DataAccessAndReport.module.css";

const CompanyTable = ({
  filtereddata,
  startIndex,
  selectedIds = [],
  onSelectionChange = () => {},
}) => {
  const getRowId = (row) => {
    return (
      row?.company_id ??
      row?.companyId ??
      row?.id ??
      row?._id ??
      row?.user?.id ??
      null
    );
  };

  const pageIds = filtereddata
    .map((row) => getRowId(row))
    .filter((id) => id !== null && id !== undefined);

  const allSelectedOnPage =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const handleHeaderCheckboxChange = (e) => {
    const checked = e.target.checked;
    if (checked) {
      const newIds = Array.from(new Set([...selectedIds, ...pageIds]));
      onSelectionChange(newIds);
    } else {
      const pageIdSet = new Set(pageIds);
      const newIds = selectedIds.filter((id) => !pageIdSet.has(id));
      onSelectionChange(newIds);
    }
  };

  const handleRowCheckboxChange = (rowId, checked) => {
    if (rowId === null || rowId === undefined) return;
    if (checked) {
      if (!selectedIds.includes(rowId)) {
        onSelectionChange([...selectedIds, rowId]);
      }
    } else {
      onSelectionChange(selectedIds.filter((id) => id !== rowId));
    }
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString();
    } catch {
      return value;
    }
  };

  const renderAvatar = (row) => {
    if (row?.user?.profile) {
      return (
        <img
          src={row.user.profile}
          alt={row.user?.name}
          className={styles.userAvatarImage}
        />
      );
    }
    return <span>🏢</span>;
  };

  const formatTokenValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }
    if (typeof value === "number") {
      return value;
    }
    if (typeof value === "string" && value.length > 12) {
      return `${value.slice(0, 9)}...`;
    }
    return value;
  };

  const getTooltipText = (value) => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const ellipsisTextStyle = {
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  };

  const columnWidths = {
    license: "110px",
    startDate: "130px",
    endDate: "130px",
    token: "140px",
    llmTokens: "140px",
    llmUsed: "140px",
    llmRemaining: "150px",
    heygenTokens: "150px",
    heygenUsed: "150px",
    heygenRemaining: "160px",
    plan: "140px",
    status: "120px",
    email: "220px",
  };

  const cellStyle = (key) => ({
    width: columnWidths[key] || "auto",
    minWidth: columnWidths[key] || "auto",
    maxWidth: columnWidths[key] || "auto",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  });

  const renderValueCell = (value, formatter) => {
    const displayValue =
      formatter && typeof formatter === "function"
        ? formatter(value)
        : value ?? "N/A";
    return (
      <span style={ellipsisTextStyle} title={getTooltipText(value)}>
        {displayValue || "N/A"}
      </span>
    );
  };

  return (
    <div className={`${styles.userTableContainer} custom-scrollbar`}>
      <div className={styles.userTableWrapper}>
        <table className={styles.userTable}>
          <thead className={styles.userThead}>
            <tr>
              <th className={styles.userThSl}>
                <div className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input me-2"
                    checked={allSelectedOnPage}
                    onChange={handleHeaderCheckboxChange}
                  />
                  <span>S.L</span>
                </div>
              </th>
              <th className={styles.userThName}>Name</th>
              <th
                className={styles.userThEmail}
                style={cellStyle("email")}
                title="Email"
              >
                <span style={ellipsisTextStyle}>Email</span>
              </th>
              <th
                className={styles.userThCompany}
                style={cellStyle("license")}
                title="License"
              >
                <span style={ellipsisTextStyle}>License</span>
              </th>
              <th
                className={styles.userThDepartment}
                style={cellStyle("startDate")}
                title="Start Date"
              >
                <span style={ellipsisTextStyle}>Start Date</span>
              </th>
              <th
                className={styles.userThDepartment}
                style={cellStyle("endDate")}
                title="End Date"
              >
                <span style={ellipsisTextStyle}>End Date</span>
              </th>
              <th
                className={styles.userThCompany}
                style={cellStyle("token")}
                title="Token"
              >
                <span style={ellipsisTextStyle}>Token</span>
              </th>
              <th
                className={styles.userThCompany}
                style={cellStyle("llmTokens")}
                title="LLM Tokens"
              >
                <span style={ellipsisTextStyle}>LLM Tokens</span>
              </th>
              <th
                className={styles.userThCompany}
                style={cellStyle("llmUsed")}
                title="LLM Used"
              >
                <span style={ellipsisTextStyle}>LLM Used</span>
              </th>
              <th
                className={styles.userThCompany}
                style={cellStyle("llmRemaining")}
                title="LLM Remaining"
              >
                <span style={ellipsisTextStyle}>LLM Remaining</span>
              </th>
              <th
                className={styles.userThCompany}
                style={cellStyle("heygenTokens")}
                title="HeyGen Tokens"
              >
                <span style={ellipsisTextStyle}>HeyGen Tokens</span>
              </th>
              <th
                className={styles.userThCompany}
                style={cellStyle("heygenUsed")}
                title="HeyGen Used"
              >
                <span style={ellipsisTextStyle}>HeyGen Used</span>
              </th>
              <th
                className={styles.userThCompany}
                style={cellStyle("heygenRemaining")}
                title="HeyGen Remaining"
              >
                <span style={ellipsisTextStyle}>HeyGen Remaining</span>
              </th>
              <th
                className={styles.userThCompany}
                style={cellStyle("plan")}
                title="Plan"
              >
                <span style={ellipsisTextStyle}>Plan</span>
              </th>
              <th
                className={styles.userThDepartment}
                style={cellStyle("status")}
                title="Status"
              >
                <span style={ellipsisTextStyle}>Status</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filtereddata.map((row, index) => {
              const isLastRow = index === filtereddata.length - 1;
              const rowId = getRowId(row);
              const isChecked =
                rowId !== null && rowId !== undefined
                  ? selectedIds.includes(rowId)
                  : false;

              return (
                <tr key={rowId ?? index} className={styles.userRow}>
                  <td
                    className={`${styles.userTdSl} ${
                      isLastRow ? styles.userTdSlLast : ""
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-3"
                        checked={isChecked}
                        onChange={(e) =>
                          handleRowCheckboxChange(rowId, e.target.checked)
                        }
                      />
                      <span className={styles.userSlText}>
                        {String(startIndex + index + 1).padStart(2, "0")}
                      </span>
                    </div>
                  </td>
                  <td className={`${styles.userTd} ${styles.userNameCell}`}>
                    <span className="d-flex align-items-center">
                      <span
                        className={`me-3 d-flex align-items-center justify-content-center ${styles.userNameAvatar}`}
                      >
                        {renderAvatar(row)}
                      </span>
                      <span className={styles.userNameText}>
                        {row?.user?.name || row?.name || "N/A"}
                      </span>
                    </span>
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userEmailCell}`}
                    style={cellStyle("email")}
                  >
                    {renderValueCell(row?.email ?? row?.user?.email, (val) =>
                      val ?? "N/A"
                    )}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userCompanyCell}`}
                    style={cellStyle("license")}
                  >
                    {renderValueCell(row?.license)}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userDepartmentCell}`}
                    style={cellStyle("startDate")}
                  >
                    {renderValueCell(row?.startDate, formatDate)}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userDepartmentCell}`}
                    style={cellStyle("endDate")}
                  >
                    {renderValueCell(row?.endDate, formatDate)}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userCompanyCell}`}
                    style={cellStyle("token")}
                  >
                    {renderValueCell(row?.token, formatTokenValue)}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userCompanyCell}`}
                    style={cellStyle("llmTokens")}
                  >
                    {renderValueCell(row?.LLM_tokens, formatTokenValue)}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userCompanyCell}`}
                    style={cellStyle("llmUsed")}
                  >
                    {renderValueCell(row?.LLM_tokens_used, formatTokenValue)}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userCompanyCell}`}
                    style={cellStyle("llmRemaining")}
                  >
                    {renderValueCell(row?.LLM_tokens_remaining, formatTokenValue)}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userCompanyCell}`}
                    style={cellStyle("heygenTokens")}
                  >
                    {renderValueCell(row?.heyGen_tokens, formatTokenValue)}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userCompanyCell}`}
                    style={cellStyle("heygenUsed")}
                  >
                    {renderValueCell(row?.heyGen_tokens_used, formatTokenValue)}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userCompanyCell}`}
                    style={cellStyle("heygenRemaining")}
                  >
                    {renderValueCell(
                      row?.heyGen_tokens_remaining,
                      formatTokenValue
                    )}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userCompanyCell}`}
                    style={cellStyle("plan")}
                  >
                    {renderValueCell(row?.plan)}
                  </td>
                  <td
                    className={`${styles.userTd} ${styles.userDepartmentCell}`}
                    style={cellStyle("status")}
                  >
                    {renderValueCell(row?.status)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyTable;
